// @ts-strict-ignore
import React from 'react';

import {
  Box,
  Button,
  IconName,
  ButtonVariant,
} from '@usealloy/component-library';

import { getCredentialOwnerOption } from './helpers/formatData';
import type { FormattedServiceConfigOption, CredentialOwner } from './types';
import { DefaultLevel } from './types';
import { ServiceConfigOptionInputField } from './ServiceConfigOptionInputFieldView';
import { ServiceConfigValidation } from './ServiceConfigValidationView';
import { AlloyOption, CustomerOption } from './helpers/fixture';

interface ServiceConfigOptionFieldProps {
  serviceConfigOptionMeta;
  formattedServiceConfigOption: FormattedServiceConfigOption;
  index: number;
  errors;
  control;
  setValue;
  trigger;
  removeServiceConfigOption;
  serviceId: number;
  credentialOwner: CredentialOwner;
  submitAutoSave?: () => {};
  onlySandboxEnabled?: boolean;
}

export const ServiceConfigOptionField = ({
  serviceConfigOptionMeta,
  formattedServiceConfigOption,
  index,
  errors,
  control,
  setValue,
  trigger,
  removeServiceConfigOption,
  serviceId,
  credentialOwner,
  submitAutoSave,
  onlySandboxEnabled,
}: ServiceConfigOptionFieldProps) => {
  const { isSecret, isOwner } = serviceConfigOptionMeta;

  const hasError = (errors?.formattedServiceConfigOptions
    && errors?.formattedServiceConfigOptions[index] !== undefined);
  const errorMessage = hasError
    ? errors?.formattedServiceConfigOptions[index].value.message : null;

  let defaultValueMessage = null;
  if (['singleselect', 'multiselect'].includes(serviceConfigOptionMeta?.datatype)) {
    // @ts-ignore
    const defaultValueArray = formattedServiceConfigOption?.defaultValue?.map(
      option => option.label,
    );
    defaultValueMessage = defaultValueArray?.join(', ');
  } else if (serviceConfigOptionMeta?.datatype === 'boolean') {
    // @ts-ignore
    defaultValueMessage = formattedServiceConfigOption?.defaultValue?.value;
  } else if (formattedServiceConfigOption?.defaultValue && isSecret) {
    defaultValueMessage = '•••••••••••';
  } else {
    defaultValueMessage = formattedServiceConfigOption?.defaultValue?.toString();
  }

  return (
    <Box
      data-dd-action-name="service-config-option-field-configuration"
      justifyContent="flex-start"
      direction="row"
      alignItems="flex-start"
    >
      <Box
        justifyContent="flex-start"
        direction="column"
        alignItems="flex-start"
      >
        <ServiceConfigOptionInputField
          key={`ServiceConfigOptionInputField-${formattedServiceConfigOption.id}`}
          serviceConfigOptionMeta={serviceConfigOptionMeta}
          formattedServiceConfigOption={formattedServiceConfigOption}
          control={control}
          index={index}
          isUsingDefault={formattedServiceConfigOption.isUsingDefault}
          hasError={hasError}
          trigger={trigger}
          setValue={setValue}
          credentialOwner={credentialOwner}
          isSecret={isSecret}
          submitAutoSave={submitAutoSave}
          onlySandboxEnabled={onlySandboxEnabled}
        />
        <ServiceConfigValidation
          isUsingDefault={formattedServiceConfigOption.isUsingDefault}
          defaultLevel={formattedServiceConfigOption.defaultLevel}
          hasError={hasError}
          errorMessage={errorMessage}
          restoreFn={() => {
            setValue(
              `formattedServiceConfigOptions.${index}.value` as const,
              formattedServiceConfigOption.defaultValue,
            );
            setValue(
              `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
              true,
            );
            trigger(`formattedServiceConfigOptions.${index}.value` as const);
            if (serviceConfigOptionMeta.isOwner
              && formattedServiceConfigOption.defaultLevel === DefaultLevel.ALLOY) {
              setValue('credentialOwnerOption', AlloyOption);
              trigger('credentialOwnerOption');
            } else if (serviceConfigOptionMeta.isOwner
              && formattedServiceConfigOption.defaultLevel === DefaultLevel.CUSTOMER) {
              if (formattedServiceConfigOption.defaultCredentialOwner) {
                setValue(
                  'credentialOwnerOption',
                  getCredentialOwnerOption(formattedServiceConfigOption.defaultCredentialOwner),
                );
              } else {
                setValue('credentialOwnerOption', CustomerOption);
              }
              trigger('credentialOwnerOption');
            }
            if (submitAutoSave) {
              submitAutoSave();
            }
          }}
          defaultValueMessage={defaultValueMessage}
          serviceId={serviceId}
          isOwner={isOwner}
        />
      </Box>
      {!serviceConfigOptionMeta.isRequired && (
        <Box width="8%" style={{ paddingTop: 'var(--cl-space-5)' }}>
          <Button
            icon={IconName.Trash}
            variant={ButtonVariant.tertiary}
            onClick={() => {
              removeServiceConfigOption(index);
              if (submitAutoSave) {
                submitAutoSave();
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};
